import { constructResponsiveImgUrl } from "../helpers";
import nondispo from "../images/imagenondisponible.png";

/**
 * ResponsiveApiImg - Reusable responsive image component for API images
 *
 * ⚠️ IMPORTANT: This component is ONLY for API images (S3/CloudFront URLs).
 * For local images (now on DigitalOcean Spaces), use ResponsiveImg instead.
 *
 * Features:
 * - Generates responsive srcSet with 5-7 breakpoints depending on useHeroSizes
 * - Automatic WebP conversion via resize service
 * - Quality/compression optimization (quality 75, compression 9)
 * - Native browser lazy loading with loading="lazy"
 * - Configurable sizes attribute for responsive selection
 * - Fallback to placeholder image when src is missing
 *
 * Use Cases:
 * - Instagram wall images (InstaWall component)
 * - Search result images (SearchInitialState component)
 * - Hero images (with useHeroSizes=true for larger breakpoints)
 * - Any API-sourced images that need responsive optimization
 *
 * @param {string} src - Image URL from API (S3/CloudFront) or NO_IMAGE constant
 * @param {string} alt - Alt text for accessibility
 * @param {string} sizes - Sizes attribute for responsive image selection
 * @param {string} className - Optional CSS class name
 * @param {string} loading - Loading strategy: "lazy" (default) or "eager"
 * @param {boolean} useHeroSizes - Use larger breakpoints for hero images (default: false)
 * @param {object} style - Optional inline styles
 *
 * @example
 * // Instagram gallery image
 * <ResponsiveApiImg
 *   src={instagramImage.url}
 *   alt="Instagram post"
 *   sizes="(max-width: 768px) 50vw, 25vw"
 *   loading="lazy"
 * />
 *
 * @example
 * // Hero image with larger breakpoints
 * <ResponsiveApiImg
 *   src={poi.media[0].url}
 *   alt="Hero image"
 *   sizes="100vw"
 *   loading="eager"
 *   useHeroSizes={true}
 * />
 */
const ResponsiveApiImg = ({
  src,
  alt,
  sizes = "100vw",
  className = "",
  loading = "lazy",
  useHeroSizes = false,
  style = {},
}) => {
  // Use placeholder image if src is missing or is the NO_IMAGE constant
  if (!src || src === "NO_IMAGE" || src.includes("imagenondisponible")) {
    return (
      <img
        src={nondispo}
        alt="not available"
        className={className}
        loading={loading}
        decoding="async"
        style={style}
      />
    );
  }

  // Generate responsive image URLs with different widths
  // Use larger breakpoints for hero images, smaller for cards/thumbnails
  const srcSet = useHeroSizes
    ? `
    ${constructResponsiveImgUrl(src, 480, 75)} 480w,
    ${constructResponsiveImgUrl(src, 768, 75)} 768w,
    ${constructResponsiveImgUrl(src, 1024, 75)} 1024w,
    ${constructResponsiveImgUrl(src, 1440, 75)} 1440w,
    ${constructResponsiveImgUrl(src, 1920, 75)} 1920w
  `.trim()
    : `
    ${constructResponsiveImgUrl(src, 320, 75)} 320w,
    ${constructResponsiveImgUrl(src, 480, 75)} 480w,
    ${constructResponsiveImgUrl(src, 600, 75)} 600w,
    ${constructResponsiveImgUrl(src, 800, 75)} 800w,
    ${constructResponsiveImgUrl(src, 1024, 75)} 1024w
  `.trim();

  const fallbackSize = useHeroSizes ? 1024 : 600;

  return (
    <img
      src={constructResponsiveImgUrl(src, fallbackSize, 75)} // Fallback for older browsers
      srcSet={srcSet}
      sizes={sizes}
      alt={alt}
      className={className}
      loading={loading}
      decoding="async"
      style={style}
    />
  );
};

export default ResponsiveApiImg;
